import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { HardHat, Loader2, ArrowLeft, Send, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeclarationFormSteps } from './DeclarationFormSteps';
import PhoneInput from './ui/PhoneInput';
import SiretInput from './ui/SiretInput';
import EmailInput from './ui/EmailInput';
import { useFormValidation } from './ui/FormField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeclarationDommageForm = ({ isOpen, onClose, getAuthHeader, memberData, onSuccess }) => {
  const { t: _t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [acceptDeclaration, setAcceptDeclaration] = useState(false);
  
  const [formData, setFormData] = useState({
    // Informations du Membre
    raison_sociale: '',
    forme_juridique: '',
    siret: '',
    adresse_complete: '',
    representant_legal: '',
    telephone: '',
    email: '',
    numero_adhesion: '',
    
    // Informations sur l'Opération
    intitule_chantier: '',
    adresse_travaux: '',
    maitre_ouvrage: '',
    nature_travaux: '',
    date_debut: '',
    date_fin: '',
    
    // Description du Dommage
    description_dommage: '',
    
    // Actions Entreprises
    actions_entreprises: '',
    
    // Demande au FOGEDOM-SCIC
    type_demande: 'expertise',
    description_demande: ''
  });

  // Pré-remplir avec les données du membre
  useEffect(() => {
    if (memberData) {
      setFormData(prev => ({
        ...prev,
        raison_sociale: memberData.raison_sociale || memberData.entreprise || '',
        forme_juridique: memberData.forme_juridique || memberData.statut_juridique || '',
        siret: memberData.siret || '',
        adresse_complete: memberData.adresse || '',
        representant_legal: `${memberData.prenom || ''} ${memberData.nom || ''}`.trim(),
        telephone: memberData.telephone || '',
        email: memberData.email || '',
        numero_adhesion: memberData.numero_membre || memberData.id || ''
      }));
    }
  }, [memberData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    setDocuments(prev => [...prev, ...files]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!acceptDeclaration) {
      toast.error('Veuillez accepter la déclaration avant de soumettre');
      return;
    }

    setLoading(true);
    try {
      // Créer un FormData pour l'upload des fichiers
      const submitData = new FormData();
      
      // Ajouter les données du formulaire
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Ajouter les photos
      photos.forEach((photo, _index) => {
        submitData.append(`photos`, photo);
      });
      
      // Ajouter les documents
      documents.forEach((doc, _index) => {
        submitData.append(`documents`, doc);
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/dommages/declaration`,
        submitData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Déclaration de dommage envoyée avec succès !');
      onSuccess && onSuccess(response.data);
      onClose();
      
      // Réinitialiser le formulaire
      setStep(1);
      setPhotos([]);
      setDocuments([]);
      setAcceptDeclaration(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la déclaration');
    } finally {
      setLoading(false);
    }
  };

  // Validation inline
  const { errors: errors, setError, clearError: clearError, clearAllErrors } = useFormValidation();

  // Validation de chaque étape avec erreurs inline
  const validateStep = () => {
    clearAllErrors();
    let isValid = true;

    switch (step) {
      case 1:
        if (!formData.raison_sociale) {
          setError('raison_sociale', 'La raison sociale est obligatoire');
          isValid = false;
        }
        if (!formData.siret) {
          setError('siret', 'Le SIRET est obligatoire');
          isValid = false;
        }
        if (!formData.email) {
          setError('email', 'L\'email est obligatoire');
          isValid = false;
        }
        if (!formData.telephone) {
          setError('telephone', 'Le téléphone est obligatoire');
          isValid = false;
        }
        break;
      case 2:
        if (!formData.intitule_chantier) {
          setError('intitule_chantier', 'L\'intitulé du chantier est obligatoire');
          isValid = false;
        }
        if (!formData.adresse_travaux) {
          setError('adresse_travaux', 'L\'adresse des travaux est obligatoire');
          isValid = false;
        }
        if (!formData.nature_travaux) {
          setError('nature_travaux', 'La nature des travaux est obligatoire');
          isValid = false;
        }
        break;
      case 3:
        if (!formData.description_dommage || formData.description_dommage.length < 50) {
          setError('description_dommage', 'La description doit contenir au moins 50 caractères');
          isValid = false;
        }
        break;
      case 4:
        if (!formData.type_demande) {
          setError('type_demande', 'Le type de demande est obligatoire');
          isValid = false;
        }
        break;
      case 5:
        if (!acceptDeclaration) {
          setError('acceptDeclaration', 'Vous devez accepter la déclaration pour continuer');
          isValid = false;
        }
        break;
      default:
        break;
    }
    return isValid;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };
  
  const prevStep = () => {
    clearAllErrors();
    setStep(prev => Math.max(prev - 1, 1));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.raison_sociale && formData.siret && formData.email && formData.telephone;
      case 2:
        return formData.intitule_chantier && formData.adresse_travaux && formData.nature_travaux;
      case 3:
        return formData.description_dommage.length >= 50;
      case 4:
        return formData.type_demande;
      case 5:
        return acceptDeclaration;
      default:
        return true;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            Déclaration de Dommage
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 px-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors
                ${step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {s}
              </div>
              {s < 5 && (
                <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="text-center text-sm text-gray-500 mb-4">
          {step === 1 && 'Informations du Membre'}
          {step === 2 && 'Informations sur l\'Opération'}
          {step === 3 && 'Description du Dommage'}
          {step === 4 && 'Demande au FOGEDOM-SCIC'}
          {step === 5 && 'Validation et Envoi'}
        </div>

        {/* Step 1: Informations du Membre */}
        <DeclarationFormSteps
          step={step}
          formData={formData}
          handleChange={handleChange}
          photos={photos}
          documents={documents}
          handlePhotoUpload={handlePhotoUpload}
          removePhoto={removePhoto}
          handleDocumentUpload={handleDocumentUpload}
          removeDocument={removeDocument}
          acceptDeclaration={acceptDeclaration}
          setAcceptDeclaration={setAcceptDeclaration}
          memberData={memberData}
        />
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : prevStep}
            disabled={loading}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {step === 1 ? 'Annuler' : 'Précédent'}
          </Button>

          {step < 5 ? (
            <Button
              onClick={nextStep}
              disabled={!isStepValid()}
            >
              Suivant
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !acceptDeclaration}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer la déclaration
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeclarationDommageForm;
